import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  Heart,
  ShoppingCart,
  Star,
  Truck,
  ShieldCheck,
  RefreshCcw
} from "lucide-react";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { currentUser } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [wishlisted, setWishlisted] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [mainImage, setMainImage] = useState("");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const docRef = doc(db, "products", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          let images = [];
          if (Array.isArray(data.images) && data.images.length > 0) {
            images = data.images.filter(url => url && url.length > 5);
          } else if (data.image) {
            images = [data.image];
          }

          setProduct({ id: docSnap.id, ...data, imageUrls: images });
          setMainImage(images[0] || "/placeholder.png");
        } else {
          setProduct(null);
        }
      } catch (err) {
        console.error(err);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) return <div className="p-6 text-center text-gray-500">Loading...</div>;
  if (!product) return <div className="p-6 text-center text-red-500">Product not found.</div>;

  const handleAddToCart = () => {
    if (!currentUser) return navigate("/signup");

    addToCart({
      ...product,
      quantity
    });

    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 2500);
  };

  const handleWishlistClick = () => setWishlisted(prev => !prev);

  const oldPrice = product.price ? (product.price * 1.3).toFixed(2) : "0.00";
  const rating = product.rating || 4.6;
  const reviews = 734;

  const images = product.imageUrls?.length ? product.imageUrls : ["/placeholder.png"];
  const sponsoredImages = [
    "https://m.media-amazon.com/images/I/71+C1pv3oYL.jpg",
    "https://m.media-amazon.com/images/I/71x8r6rQPxL._AC_SL1500_.jpg",
    "https://m.media-amazon.com/images/I/61Elep9NzxL._AC_UF894,1000_QL80_.jpg",
    "https://ejazkhancinema.com/wp-content/uploads/2023/08/DSLR-Filmmaking-e1691786892755.jpeg",
    "https://brother-mart.com/cdn/shop/files/Newly-Luanched-Redmi-A3-Smartphone-availablle-at-Brother-mart.jpg?v=1710154219"
  ];

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col relative p-0 m-0 pb-[75px]">
       {/* Back to Home Button */}
      {/* <button
        onClick={() => navigate("/")}
        className="m-2 px-2 py-2 w-20  bg-[#009395] text-white rounded-md hover:bg-blue-700 transition"
      >
        Back 
      </button> */}

      {/* Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-80 text-center relative animate-scaleIn">
            <h2 className="text-2xl font-bold mb-2 text-green-600">Product Added!</h2>
            <p className="text-gray-700 mb-4">{product.name} has been added to your cart.</p>
            <span className="absolute bottom-3 left-3 text-sm text-gray-500">
              🚚 Fast Delivery, 323 sold
            </span>
          </div>
        </div>
      )}

      {/* Desktop Layout */}
      <div className="mt-4 max-w-7xl mx-auto px-4 md:px-8 flex flex-col lg:flex-row gap-8">
        {/* Left: Main Image */}
        <div className="flex-1 flex flex-col items-center">
          <div className="hidden lg:flex w-full bg-white rounded-lg shadow p-4 justify-center items-center">
            <img
              src={mainImage}
              alt={product.name}
              className="object-contain max-h-[500px] w-full"
            />
          </div>

          {/* Thumbnails */}
          <div className="hidden lg:flex flex-wrap mt-4 gap-2">
            {images.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`${product.name}-${idx}`}
                className={`w-20 h-20 object-cover rounded border cursor-pointer ${
                  mainImage === img ? "border-blue-500" : "border-gray-200"
                }`}
                onClick={() => setMainImage(img)}
              />
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="lg:hidden overflow-x-auto flex space-x-0 ">
            {images.map((img, idx) => (
              <div
                key={idx}
                className="relative flex-shrink-0 w-full max-w-md h-[400px] rounded-lg border-[8px] border-white  bg-[#e3e6e7] p-0 md:p-8"
              >
                <img
                  src={img}
                  alt={`${product.name}-${idx}`}
                  className="w-full h-full object-cover rounded-lg"
                />
                <div className="absolute top-3 right-3">
                  <button
                    onClick={handleWishlistClick}
                    className="p-2 bg-white bg-opacity-70 rounded-full hover:bg-opacity-100 transition"
                  >
                    <Heart
                      className={`w-5 h-5 ${wishlisted ? "text-red-500 fill-red-500" : "text-gray-400"}`}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Product Info */}
        <div className="flex-1 flex flex-col gap-4">
          <h1 className="text-3xl font-semibold">{product.name}</h1>

          <div className="flex items-center gap-3">
            <p className="text-3xl font-bold text-red-600">₹{product.price}</p>
            <p className="line-through text-gray-500 text-lg">₹{oldPrice}</p>
            <span className="bg-yellow-300 px-2 py-1 rounded text-sm font-semibold">30% OFF</span>
          </div>

          <div className="flex items-center gap-2 text-yellow-500">
            <Star className="fill-yellow-400" />
            <span className="text-black">{rating} | {reviews} reviews</span>
          </div>

          {/* Shipping / Returns / Payment */}
          <div className="mt-6 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Truck className="w-6 h-6 text-gray-700" />
              <div>
                <p className="font-semibold">Shipping Available</p>
                <p className="text-sm text-gray-600">Delivery within 3–5 days. Charges apply.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <RefreshCcw className="w-6 h-6 text-gray-700" />
              <div>
                <p className="font-semibold">Return Policy</p>
                <p className="text-sm text-gray-600">7-day replacement if product is damaged or incorrect.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-gray-700" />
              <div>
                <p className="font-semibold">Secure Payment</p>
                <p className="text-sm text-gray-600">Your transaction is encrypted and protected.</p>
              </div>
            </div>
          </div>

          {/* Desktop Buttons */}
          <div className="hidden lg:flex gap-3 mt-4">
            <button
              onClick={handleAddToCart}
              className="flex-1 py-3 rounded-xl bg-yellow-500 text-white font-medium hover:bg-yellow-600 flex items-center justify-center space-x-2"
            >
              <ShoppingCart className="w-5 h-5" /> Add to Cart
            </button>
            <button
              onClick={() => navigate("/payment")}
              className="flex-1 py-3 rounded-xl bg-green-600 text-white font-medium hover:bg-green-700"
            >
              Buy Now
            </button>
          </div>

          {/* Description */}
          <div className="mt-6 text-gray-700">
            <h2 className="text-xl font-semibold mb-2">Product Description</h2>
            <p>{product.description}</p>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white p-3 flex gap-3 shadow border-t z-50 pb-[max(env(safe-area-inset-bottom),12px)]">
        <button
          onClick={handleAddToCart}
          className="flex-1 py-3 rounded-xl bg-yellow-500 text-white font-medium hover:bg-yellow-600 flex items-center justify-center space-x-2"
        >
          <ShoppingCart className="w-5 h-5" /> Add to Cart
        </button>
        <button
          onClick={() => navigate("/payment")}
          className="flex-1 py-3 rounded-xl bg-green-600 text-white font-medium hover:bg-green-700"
        >
          Buy Now
        </button>
      </div>

      {/* Sponsored Products */}
      <div className="w-full px-4 md:px-8 mt-10">
        <h2 className="text-xl font-semibold mb-3">Sponsored Products</h2>
        <div className="w-full overflow-x-auto overflow-y-hidden no-scrollbar" style={{ WebkitOverflowScrolling: "touch" }}>
          <div className="flex gap-3">
            {sponsoredImages.map((img, idx) => (
              <div key={idx} className="min-w-32 max-w-32 h-32 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                <img src={img} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scaleIn {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;
